import { DatePicker } from "rsuite";
import ReactSelect from "react-select";
import { formatString } from "../../../../components/Helper";
import { SalesInvoiceStatus } from "../../../../components/Enums";
import { ErrorMessage, Field, FormikErrors, FormikValues } from "formik";
import "rsuite/DateRangePicker/styles/index.css";

interface BasicFieldsProps {
  salesContractList: any;
  buyerList: any;
  logisticList: any;
  chaList: any;
  notifyPartyList: any;
  setAddLogisticModalStatus: (show: boolean) => void;
  setAddChaModalStatus: (show: boolean) => void;
  setAddNotifyPartyModalStatus(show: boolean): void;
  values: FormikValues;
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean,
  ) => Promise<void | FormikErrors<FormikValues>>;
  resetForm: any;
  handleChange: (e: React.ChangeEvent<any>) => void;
  handleSalesContractChange: any;
  handleBuyerChange?: any;
  permissions: any;
}

const BasicFields: React.FC<BasicFieldsProps> = ({
  salesContractList,
  buyerList,
  logisticList,
  chaList,
  notifyPartyList,
  setAddLogisticModalStatus,
  setAddChaModalStatus,
  setAddNotifyPartyModalStatus,
  values,
  setFieldValue,
  resetForm,
  handleChange,
  handleSalesContractChange,
  handleBuyerChange,
  permissions,
}) => {
  return (
    <div className="card my-card">
      <div className="card-header my-card-header bg-lighter">
        <div>
          <h6 className="my-card-header-head">Basic Details</h6>
        </div>
      </div>
      <div className="card-body my-card-body">
        <div className="row g-3 m-0">
          <div className="col-12 col-sm-6 col-md-4 col-lg-3">
            <label
              htmlFor="buyer_id"
              className="form-label"
              style={{ fontSize: "0.9375rem" }}
            >
              Buyer: <span className="text-danger">*</span>
            </label>
            <select
              name="buyer_id"
              className="form-select"
              id="buyer_id"
              value={values.buyer_id}
              onChange={(e: any) => {
                handleChange(e);
                handleBuyerChange?.(e.target.value, setFieldValue); // ✅ CALL THIS
              }}
            >
              <option value="">Select Buyer</option>
              {buyerList.map((e: any) => (
                <option value={e.id} key={e.id}>
                  {e.name}
                </option>
              ))}
            </select>
          </div>

          <div className="col-12 col-sm-6 col-md-4 col-lg-3">
            <label
              htmlFor="sales_contract_id"
              className="form-label"
              style={{ fontSize: "0.9375rem" }}
            >
              Sales Contract: <span className="text-danger">*</span>
            </label>
            <ReactSelect
              isMulti
              classNamePrefix="react-select"
              placeholder="Select Sales Contract"
              closeMenuOnSelect={false}
              options={salesContractList.map((c: any) => ({
                label: c.contract_no,
                value: c.id,
              }))}
              value={salesContractList
                .filter((c: any) => values.sales_contract_ids?.includes(c.id))
                .map((c: any) => ({
                  label: c.contract_no,
                  value: c.id,
                }))}
              onChange={(selected: any) => {
                const ids = selected ? selected.map((s: any) => s.value) : [];

                setFieldValue("sales_contract_ids", ids);

                handleSalesContractChange(ids, setFieldValue, values);
              }}
            />
            <ErrorMessage
              name="sales_contract_ids"
              className="text-danger"
              component="div"
            />
          </div>

          <div className="col-12 col-sm-6 col-md-4 col-lg-3">
            <label
              htmlFor="invoice_no"
              className="form-label"
              style={{ fontSize: "0.9375rem" }}
            >
              Invoice No: <span className="text-danger">*</span>
            </label>
            <Field
              id="invoice_no"
              name="invoice_no"
              type="text"
              className="form-control"
              placeholder="Enter Invoice No"
              value={values.invoice_no}
              onChange={handleChange}
            />
            <ErrorMessage
              name="invoice_no"
              className="text-danger"
              component="div"
            />
          </div>
          <div className="col-12 col-sm-6 col-md-4 col-lg-3">
            <label
              htmlFor="invoice_date"
              className="form-label"
              style={{ fontSize: "0.9375rem" }}
            >
              Invoice Date: <span className="text-danger">*</span>
            </label>
            <DatePicker
              id="invoice_date"
              name="invoice_date"
              format="dd-MM-yyyy"
              className="form-control"
              placeholder="Select Invoice Date"
              value={values.invoice_date}
              onChange={(value: any) => {
                setFieldValue("invoice_date", value);
              }}
              onSelect={(value) => {
                setFieldValue("invoice_date", value);
              }}
            />
            <ErrorMessage
              name="invoice_date"
              className="text-danger"
              component="div"
            />
          </div>
          <div className="col-12 col-sm-6 col-md-4 col-lg-3">
            <div className="d-flex justify-content-between">
              <div>
                <label
                  htmlFor="logistic_id"
                  className="form-label"
                  style={{ fontSize: "0.9375rem" }}
                >
                  Forwarder:
                </label>
              </div>
              {permissions.includes("create-logistic") && (
                <div>
                  <button
                    title="Add Logistic"
                    className="btn btn-sm btn-primary waves-effect waves-light p-0"
                    type="button"
                    onClick={() => setAddLogisticModalStatus(true)}
                  >
                    <span>
                      <i className="ti ti-plus my-card-icon-button"></i>
                    </span>
                  </button>
                </div>
              )}
            </div>
            <select
              name="logistic_id"
              className="form-select"
              id="logistic_id"
              value={values.logistic_id}
              onChange={handleChange}
            >
              <option defaultChecked value={""}>
                Select Forwarder
              </option>
              {logisticList.map((e: any) => {
                return (
                  <option value={e.id} key={e.id}>
                    {e.name}
                  </option>
                );
              })}
            </select>
          </div>
          <div className="col-12 col-sm-6 col-md-4 col-lg-3">
            <div className="d-flex justify-content-between">
              <div>
                <label
                  htmlFor="cha_id"
                  className="form-label"
                  style={{ fontSize: "0.9375rem" }}
                >
                  CHA:
                </label>
              </div>
              {permissions.includes("create-cha") && (
                <div>
                  <button
                    title="Add Cha"
                    className="btn btn-sm btn-primary waves-effect waves-light p-0"
                    type="button"
                    onClick={() => setAddChaModalStatus(true)}
                  >
                    <span>
                      <i className="ti ti-plus my-card-icon-button"></i>
                    </span>
                  </button>
                </div>
              )}
            </div>
            <select
              name="cha_id"
              className="form-select"
              id="cha_id"
              value={values.cha_id}
              onChange={handleChange}
            >
              <option defaultChecked value={""}>
                Select CHA
              </option>
              {chaList.map((e: any) => {
                return (
                  <option value={e.id} key={e.id}>
                    {e.name}
                  </option>
                );
              })}
            </select>
          </div>

          <div className="col-12 col-sm-6 col-md-4 col-lg-3">
            <label
              htmlFor="status"
              className="form-label"
              style={{ fontSize: "0.9375rem" }}
            >
              Status: <span className="text-danger">*</span>
            </label>
            <select
              id="status"
              name="status"
              className="form-select"
              value={values.status}
              onChange={handleChange}
            >
              <option defaultChecked value={""}>
                Select Status
              </option>
              <option
                key={SalesInvoiceStatus.PENDING}
                value={SalesInvoiceStatus.PENDING}
              >
                {formatString(SalesInvoiceStatus.PENDING)}
              </option>
              <option
                key={SalesInvoiceStatus.IN_TRANSIT}
                value={SalesInvoiceStatus.IN_TRANSIT}
              >
                {formatString(SalesInvoiceStatus.IN_TRANSIT)}
              </option>
              <option
                key={SalesInvoiceStatus.COMPLETED}
                value={SalesInvoiceStatus.COMPLETED}
              >
                {formatString(SalesInvoiceStatus.COMPLETED)}
              </option>
            </select>
            <ErrorMessage
              name="status"
              className="text-danger"
              component="div"
            />
          </div>

          {!values.is_notify_party ? (
            <div className="col-12 col-sm-6 col-md-4 col-lg-3 d-flex align-items-center">
              <div className="form-check m-0 mt-5">
                <Field
                  type="checkbox"
                  id="is_notify_party"
                  name="is_notify_party"
                  checked={values.is_notify_party}
                  onChange={(e: any) => {
                    handleChange(e);
                    setFieldValue("notify_party_id", "");
                  }}
                  className="form-check-input"
                />
                <label
                  htmlFor="notify_party_id"
                  className="form-label"
                  style={{ fontSize: "0.9375rem" }}
                >
                  Notify Party
                </label>
              </div>
            </div>
          ) : (
            <div className="col-12 col-sm-6 col-md-4 col-lg-3">
              <div className="d-flex justify-content-between">
                <div>
                  <Field
                    type="checkbox"
                    id="is_notify_party"
                    name="is_notify_party"
                    checked={values.is_notify_party || ""}
                    onChange={(e: any) => {
                      handleChange(e);
                      setFieldValue("notify_party_id", "");
                    }}
                    className="form-check-input me-2"
                  />
                  <label
                    htmlFor="notify_party_id"
                    className="form-label"
                    style={{ fontSize: "0.9375rem" }}
                  >
                    Notify Party: <span className="text-danger">*</span>
                  </label>
                </div>
                {permissions.includes("create-notify-party") && (
                  <div>
                    <button
                      title="Add Notify Party"
                      className="btn btn-sm btn-primary waves-effect waves-light p-0"
                      type="button"
                      onClick={() => setAddNotifyPartyModalStatus(true)}
                    >
                      <span>
                        <i className="ti ti-plus my-card-icon-button"></i>
                      </span>
                    </button>
                  </div>
                )}
              </div>
              <select
                name="notify_party_id"
                className="form-select"
                id="notify_party_id"
                value={values.notify_party_id}
                onChange={handleChange}
              >
                <option
                  defaultChecked
                  value={""}
                  disabled={values.notify_party_id}
                >
                  Select Notify Party
                </option>
                {notifyPartyList.map((e: any) => {
                  return (
                    <option value={e.id} key={e.id}>
                      {e.name}
                    </option>
                  );
                })}
              </select>
              <ErrorMessage
                name="notify_party_id"
                className="text-danger"
                component="div"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BasicFields;
